import { useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useSetAtom } from "jotai/react";
import { MailIcon } from "lucide-react-native";
import { toast } from "sonner-native";

import { ApplicationScreenLayout } from "@/blocks/ApplicationScreenLayout";
import { InfoCard } from "@/blocks/InfoCard";
import { ScreenLayout } from "@/blocks/ScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnIcon } from "@/components/VnIcon";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateApplicationMutation } from "@/data/mutations/applicationsMutations";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { useListApplicationsQuery } from "@/data/queries/applicationsQueries";
import { APPLICATION_FLOW_DEFAULT_STEPS } from "@/domain/applications";
import { useTranslator } from "@/hooks/useTranslate";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
  submissionConfirmationApplicationIdsAtom,
} from "@/stores/atoms";
import { logger } from "@/utils/logger";

const SubmitContent = () => {
  const { t } = useTranslator();
  const router = useRouter();
  const { applicationIds: routeApplicationIds } = useLocalSearchParams<{
    applicationIds?: string;
  }>();

  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const setApplicationTotalSteps = useSetAtom(applicationTotalStepsAtom);
  const setSubmissionConfirmationApplicationIds = useSetAtom(
    submissionConfirmationApplicationIdsAtom,
  );

  const { data: dbApplications } = useListApplicationsQuery({
    filters: { status: "DRAFT" },
    isEnabled: !routeApplicationIds || routeApplicationIds.length === 0,
  });

  const applicationIds =
    routeApplicationIds ?? dbApplications?.map(app => app.id).join(",");

  // Parse comma-separated IDs into array
  const parsedIds = applicationIds?.split(",").filter(Boolean) ?? [];

  const { mutateAsync: updateApplication } = useUpdateApplicationMutation();
  const { mutateAsync: updateCurrentPatient } =
    useUpdateCurrentPatientMutation();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmitButton = async () => {
    if (!parsedIds.length) {
      return;
    }

    setIsLoading(true);

    try {
      // Submit all applications
      const results = await Promise.allSettled(
        parsedIds.map(id =>
          updateApplication({
            id,
            applicationData: {
              applicationStatus: "SUBMITTED",
            },
          }),
        ),
      );

      const hasFailures = results.some(result => result.status === "rejected");
      const hasSuccesses = results.some(
        result => result.status === "fulfilled",
      );

      // allSettled preserves input order, so result indexes map to parsedIds.
      const submittedIds = parsedIds.filter(
        (_, index) => results[index]?.status === "fulfilled",
      );
      if (submittedIds.length > 0) {
        setSubmissionConfirmationApplicationIds(submittedIds);
      }

      if (hasFailures && hasSuccesses) {
        // Partial failure
        toast.warning(t("native:applicationSubmitScreen.partialFailure"));
      } else if (hasFailures) {
        // All failed
        toast.error(t("common:errors.unableToCreateApplications"));
      } else {
        // All succeeded
        toast.success(t("native:applicationSubmitScreen.success"));
      }

      try {
        await updateCurrentPatient({
          patientData: {
            applicationLastStep: null,
          },
        });
      } catch (error) {
        // Clearing applicationLastStep failed — log to Sentry but don't block
        // navigation since submissions already succeeded and can't be undone
        logger.error({
          message: "Failed to clear applicationLastStep after submission",
          error,
        });
        toast.error(t("common:errors.unknown"));
      }

      // Show the confirmation screen only when all submissions succeed; otherwise return to the main authed screen.
      // Pass the submitted application ids through so the NPS survey can link them.
      router.replace(
        hasFailures
          ? "/(authed)"
          : {
              pathname: "/(authed)/(modal)/application/confirmation",
              params: { applicationIds: parsedIds.join(",") },
            },
      );

      // Reset application flow step on exit
      setApplicationCurrentStep(1);
      setApplicationTotalSteps(APPLICATION_FLOW_DEFAULT_STEPS);
    } catch {
      toast.error(t("common:errors.unableToCreateApplications"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <VnBox className="flex flex-col items-center gap-6">
        <VnBox className="items-center gap-3">
          <VnBox className="bg-input items-center justify-center rounded-full p-4">
            <VnIcon as={MailIcon} size={26} />
          </VnBox>
          <VnText
            i18nKey="native:applicationSubmitScreen.title"
            variant="headerOne"
          />
          <VnText
            i18nKey="native:applicationSubmitScreen.description"
            variant="lg"
            className="text-muted-foreground text-center"
          />
        </VnBox>
        <InfoCard titleI18nKey="native:applicationSubmitScreen.hintMessage" />
      </VnBox>
      <VnButton
        i18nKey="native:applicationSubmitScreen.submitButton"
        isLoading={isLoading}
        className="mx-4 mt-12"
        onPress={() => {
          void handleSubmitButton();
        }}
      />
    </>
  );
};

export const SubmitScreen = () => {
  return (
    <ScreenLayout className="mx-auto w-full max-w-lg gap-6 pb-6 pt-6">
      <SubmitContent />
    </ScreenLayout>
  );
};

export const ApplicationSubmitScreen = () => {
  return (
    <ApplicationScreenLayout shouldShowNextButton={false}>
      <SubmitContent />
    </ApplicationScreenLayout>
  );
};
